/**
 * SecondSold 채팅 헬퍼 함수들
 *
 * 기존 채팅 시스템의 유틸리티 함수들을 참조하여 구현된
 * SecondSold 채팅 전용 헬퍼 함수들입니다.
 */
import dayjs from 'dayjs';

import type { DomainChatResponse, DomainChatRoomResponse } from '../../apis/acloset/Swagger';
import type { SecondSoldChatRoom, SecondSoldMessage } from '../SecondSoldChat';

// 기존 유틸리티 재사용

/**
 * API 채팅 응답을 SecondSoldMessage 형태로 변환
 *
 * DomainChatResponse를 SecondSoldMessage로 변환합니다.
 *
 * @param apiResponse API에서 받은 채팅 응답
 * @returns SecondSold 메시지 형식
 */
export const convertApiResponseToMessage = (apiResponse: DomainChatResponse): SecondSoldMessage => {
  return {
    id: apiResponse.chatID,
    chatroomID: apiResponse.roomID, // API에서 roomID 제공
    senderID: apiResponse.userID, // API에서 userID 제공
    senderNickname: apiResponse.userName, // API에서 userName 제공
    content: apiResponse.content || '',
    messageType: apiResponse.contentType === 'CHAT_CONTENT-TYPE_IMAGE' ? 'image' : 'text',
    createdAt: new Date(apiResponse.createdAt).getTime(),
    metadata: apiResponse.metadata,
    fileURL: apiResponse.contentType === 'CHAT_CONTENT-TYPE_IMAGE' ? apiResponse.content : undefined,
  };
};

/**
 * API 응답을 SecondSoldChatRoom 형태로 변환
 *
 * DomainChatRoomResponse를 SecondSoldChatRoom으로 변환합니다.
 * 참여자 정보는 메시지에서 추론하거나 별도로 설정해야 합니다.
 *
 * @param apiResponse API에서 받은 채팅방 응답
 * @param currentUserID 현재 사용자 ID (참여자 추론용)
 * @returns SecondSold 채팅방 형식
 */
export const convertApiResponseToChatRoom = (apiResponse: DomainChatRoomResponse): SecondSoldChatRoom => {
  return {
    roomID: apiResponse.roomID,
    lastMessage: apiResponse.lastChatContents,
    lastMessageTime: apiResponse.lastChattedAt ? new Date(apiResponse.lastChattedAt).getTime() : undefined,
    unreadCount: apiResponse.unreadChatCount || 0, // API에서 제공되는 읽지 않은 메시지 개수
  };
};

/**
 * Parse chat room key to extract IDs
 * - PROSELLER: `:sellerID:buyerID`
 * - NORMAL-SELLER-MARKET-ITEM: `:marketItemID:sellerID:`
 */
export const parseChatRoomKey = (room: DomainChatRoomResponse) => {
  const parts = room.chatRoomKey.split(':').filter(Boolean);

  if (room.chatRoomType === 'CHAT_ROOM-TYPE_PROSELLER' && parts.length === 2) {
    const [sellerID, buyerID] = parts;
    return {
      sellerID: sellerID ? parseInt(sellerID, 10) : undefined,
      buyerID: buyerID ? parseInt(buyerID, 10) : undefined,
      marketItemID: undefined,
    };
  }

  return {
    sellerID: undefined,
    buyerID: undefined,
    marketItemID: undefined,
  };
};

/**
 * 메시지 시간을 사용자 친화적 형태로 포맷
 *
 * 기존 Sendbird 채팅과 동일한 시간 표시 형식을 사용합니다.
 *
 * @param timestamp Unix timestamp (밀리초)
 * @returns 포맷된 시간 문자열 (예: "14:30", "어제", "2024.01.15")
 */
export const formatMessageTime = (timestamp: number): string => {
  const messageTime = dayjs(timestamp);
  const now = dayjs();
  const today = now.startOf('day');
  const yesterday = today.subtract(1, 'day');

  // 오늘 메시지인 경우: 시:분 형태
  if (messageTime.isAfter(today)) {
    return messageTime.format('HH:mm');
  }

  // 어제 메시지인 경우: "어제" 텍스트
  if (messageTime.isAfter(yesterday)) {
    return '어제';
  }

  // 그 이전 메시지: 날짜 형태
  return messageTime.format('YYYY.MM.DD');
};

/**
 * 내가 보낸 메시지인지 확인
 *
 * @param message 메시지 객체
 * @param currentUserID 현재 사용자 ID
 * @returns 내가 보낸 메시지인지 여부
 */
export const isMyMessage = (message: SecondSoldMessage, currentUserID: number): boolean => {
  return message.senderID === currentUserID;
};

/**
 * 메시지 전송 가능 여부 검증
 *
 * @param content 메시지 내용
 * @returns 전송 가능 여부와 에러 메시지
 */
export const validateMessageContent = (
  content: string,
): {
  isValid: boolean;
  errorMessage?: string;
} => {
  // 빈 메시지 검증
  if (!content || content.trim().length === 0) {
    return {
      isValid: false,
      errorMessage: '메시지를 입력해주세요.',
    };
  }

  // 메시지 길이 검증 (최대 2000자)
  if (content.length > 2000) {
    return {
      isValid: false,
      errorMessage: '메시지는 2000자 이내로 입력해주세요.',
    };
  }

  return {
    isValid: true,
  };
};

/**
 * 파일 업로드 가능 여부 검증
 *
 * @param file 업로드할 파일
 * @returns 업로드 가능 여부와 에러 메시지
 */
export const validateFileUpload = (
  file: File,
): {
  isValid: boolean;
  errorMessage?: string;
} => {
  // 파일 크기 검증 (최대 10MB)
  const MAX_FILE_SIZE = 10 * 1024 * 1024;
  if (file.size > MAX_FILE_SIZE) {
    return {
      isValid: false,
      errorMessage: '파일 크기는 10MB 이하여야 합니다.',
    };
  }

  // 이미지 파일 타입 검증
  const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];

  if (!ALLOWED_TYPES.includes(file.type)) {
    return {
      isValid: false,
      errorMessage: '지원되지 않는 파일 형식입니다. (JPEG, PNG, WebP, GIF만 가능)',
    };
  }

  return {
    isValid: true,
  };
};
